import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import { timeToStringWithTimezone } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import type { Shift } from 'modules/shift_management/lib/types'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

import BreakDuration from './BreakDuration'
import BreakHourRange from './BreakHourRange'
import BreakType from './BreakType'

interface Props {
  plannedBreak: PlannedBreak
  canManage: boolean
  shift?: Shift
  showDeleted?: boolean
  newVersion?: boolean
}

const Item = ({
  plannedBreak,
  canManage,
  shift,
  showDeleted = false,
  newVersion = false,
}: Props) => {
  const { breakConfiguration, duration, startAt, endAt, isFlexible, isSemiFlexible, isFixed } =
    plannedBreak
  const { name, paid, archived } = breakConfiguration || {}
  const timezone = shift?.employeeTimeZone || 'Utc'
  const from = startAt && timeToStringWithTimezone(moment(startAt), timezone)
  const to = endAt && timeToStringWithTimezone(moment(endAt), timezone)
  const showDuration = !!duration && (isFlexible || isSemiFlexible)
  const showHourRange = !!from && !!to && (isFixed || isSemiFlexible)

  if (!newVersion) {
    return (
      <Box flexDirection="column" gap="s8">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <Text size="300" color="grey1000" ellipsis>
            {name}
          </Text>
          {canManage && <BreakType paid={!!paid} />}
        </Box>
        {showDuration && <BreakDuration duration={duration} />}
        {showHourRange && <BreakHourRange from={from} to={to} />}
      </Box>
    )
  }

  return (
    <Box gap="s16" flexDirection="row">
      <Box paddingY="s16">
        <TooltipIcon.Medium
          icon="coffee"
          label={i18n.t('shift_management.actions_sidepanel.tooltip.breaks')}
          color="grey700"
        />
      </Box>

      <Box width="full">
        <Box borderRadius={{ all: 'abs016' }} background="grey100">
          {archived && showDeleted ? (
            <Box paddingY="s4">
              <Callout
                type="info"
                description={i18n.t('shift_management.planned_breaks.archived.was_deleted', {
                  name: plannedBreak.breakConfiguration?.name,
                })}
                hideIcon
                compact
              />
            </Box>
          ) : (
            <Box padding="s16" gap="s8">
              <Box gap="s4">
                <Text.Normal color="grey1000" ellipsis>
                  {name}
                </Text.Normal>
                {canManage && <BreakType paid={!!paid} />}
              </Box>
              <Box paddingY="s2">
                <Divider />
              </Box>
              <Box gap="s6">
                {showDuration && <BreakDuration duration={duration} />}
                {showHourRange && (
                  <BreakHourRange from={from} to={to} isFixed={isFixed} newVersion />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default observer(Item)
